import { useCallback } from 'react'
import { useGroupsContext } from '@fs/zion-tree-groups'
import { usePersonPageParams } from '../person-page/personPageRouteUtil'
import { useFocusPID } from '../providers/FocusPIDContext'
import { mutateFocusPerson } from './useFocusPerson'
import useOrdinances from './useOrdinances'

/**
 * Returns a callback that refreshes the ordinance data and, after a delay, the focus person's
 * temple rollup status. Call it after any change that can affect ordinance qualification
 * (vitals, parent-child or couple relationships, added persons).
 *
 * Despite waiting for the ordinances refetch to resolve, a timeout is still needed for the rollup
 * status to propagate appropriately. Currently we don't get a CALCULATING rollup status back from
 * the reservations team after one of these changes is made, so waiting a few seconds is generally
 * long enough that the rollup status will have officially changed.
 * JIRA: https://fhjira.churchofjesuschrist.org/browse/TW-5161
 */
export default function useTempleRollupRefresh(): () => void {
  const { pid: paramsPid } = usePersonPageParams('full')
  // When rendered inside a MemoryRouter (e.g. the relationship dialogs), the route 'pid' param
  // doesn't exist, so fall back to the FocusPIDContext (same hazard as in useFocusPerson).
  const focusPID = useFocusPID()
  const pid = paramsPid || focusPID
  const { currentTreeId } = useGroupsContext()
  const { mutateOrdinances } = useOrdinances()

  return useCallback(() => {
    // Without a pid (neither the route param nor the FocusPIDContext), mutateFocusPerson would
    // build an invalid SWR key like /person/null/card, so skip the refresh entirely.
    if (!pid) return
    mutateOrdinances()
      .then(() => {
        return setTimeout(() => {
          return mutateFocusPerson(pid, currentTreeId || 'PRIVATE')
        }, 5000)
      })
      .catch((err) => console.warn(err))
  }, [currentTreeId, mutateOrdinances, pid])
}
